'use client';
import React from 'react';
import Link from 'next/link';
import {
  UtensilsCrossed,
  Stethoscope,
  GraduationCap,
  Building2,
  ShoppingBag,
  Scale,
  Laptop,
  Rocket,
  ArrowRight,
  Sparkles,
} from 'lucide-react';

export const TTOSIndustriesV2: React.FC = () => {
  const industries = [
    {
      name: 'Restaurant',
      desc: 'Menus, food reels, local SEO, reservations, offers',
      icon: UtensilsCrossed,
      color: 'text-amber-600 bg-amber-50',
    },
    {
      name: 'Doctor',
      desc: 'Patient engagement, health content, local SEO',
      icon: Stethoscope,
      color: 'text-emerald-600 bg-emerald-50',
    },
    {
      name: 'School',
      desc: 'Admissions, courses, student engagement',
      icon: GraduationCap,
      color: 'text-blue-600 bg-blue-50',
    },
    {
      name: 'Real Estate',
      desc: 'Property listings, virtual tours, lead generation',
      icon: Building2,
      color: 'text-rose-600 bg-rose-50',
    },
    {
      name: 'Retail',
      desc: 'Product showcases, offers, reels, campaigns',
      icon: ShoppingBag,
      color: 'text-pink-600 bg-pink-50',
    },
    {
      name: 'Lawyer',
      desc: 'Legal content, client trust, local SEO',
      icon: Scale,
      color: 'text-purple-600 bg-purple-50',
    },
    {
      name: 'Freelancer',
      desc: 'Portfolio, personal brand, client management',
      icon: Laptop,
      color: 'text-indigo-600 bg-indigo-50',
    },
    {
      name: 'Startup',
      desc: 'Pitch decks, growth hacking, brand building',
      icon: Rocket,
      color: 'text-sky-600 bg-sky-50',
    },
  ];

  return (
    <section className="py-20 bg-slate-50/50 border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 gap-4">
          <div className="space-y-1">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Industry-Specific Examples
            </h2>
            <p className="text-sm text-slate-500">
              Get ready-made templates, content, keywords and campaigns for your industry
            </p>
          </div>
          <Link
            href="/register"
            className="text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 shrink-0"
          >
            <span>View All Industries</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* 8 Industry Cards Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {industries.map((ind, idx) => (
            <div
              key={idx}
              className="bg-white rounded-2xl p-3.5 border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-200 flex flex-col items-center text-center justify-between group hover:-translate-y-1"
            >
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2.5 ${ind.color}`}>
                  <ind.icon className="w-5 h-5" />
                </div>
                <h3 className="text-xs font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">
                  {ind.name}
                </h3>
                <p className="text-[10px] text-slate-400 mt-1 line-clamp-2 leading-tight">
                  {ind.desc}
                </p>
              </div>

              <Link
                href="/register"
                className="mt-3 text-[10.5px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-0.5 group-hover:underline"
              >
                <span>Explore</span>
                <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          ))}
        </div>

        {/* Bottom CTA Card (matching Screenshot 2 & 3) */}
        <div className="mt-14 rounded-3xl overflow-hidden bg-gradient-to-r from-purple-800 via-indigo-800 to-purple-950 p-8 sm:p-10 text-white shadow-xl relative">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-2 text-center md:text-left">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-purple-200 text-xs font-semibold">
                <Sparkles className="w-3 h-3 text-purple-300" />
                <span>Ready to Build Your AI-Powered Workspace?</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-black text-white">
                Don&apos;t Prompt. Just Choose.
              </h3>
              <p className="text-xs sm:text-sm text-purple-200">
                Tell us your industry and business details, TT OS will handle the rest.
              </p>
            </div>

            <div className="flex flex-col items-center md:items-end gap-3 shrink-0">
              <Link
                href="/register"
                className="px-7 py-3 rounded-full bg-white text-purple-800 hover:bg-slate-50 text-xs sm:text-sm font-extrabold shadow-lg hover:scale-105 transition-transform flex items-center gap-2"
              >
                <span>Build My Workspace</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
              <div className="flex items-center gap-3 text-[10.5px] text-purple-200">
                <span>Free to start</span>
                <span>•</span>
                <span>No credit card required</span>
                <span>•</span>
                <span>Setup in 2 minutes</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default TTOSIndustriesV2;
